import { useMemo, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import {
  Cell,
  PieChart,
  Pie,
  Tooltip,
  ResponsiveContainer,
  Sector,
  Legend,
} from 'recharts';
import { Box } from '@mui/system';
// import SouthEastIcon from '@mui/icons-material/SouthEast';
// import NorthEastIcon from '@mui/icons-material/NorthEast';
import { FlexBox } from 'components/Containers/FlexBox';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import { truncateTo2dp } from 'utils/helpers/general';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import TableList from 'views/Utilization/components/TableList';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import { getFuelUsageEventLabel } from '../utils';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  title: string;
  isLoading: boolean;
  customerCode: string;
  data: Record<string, number>;
  setHoursBreakDown: any;
  totalFuelConsumption: number;
  assetTypeKey: string;
}

const FuelUsageBreakdown = ({
  enableDarkTheme,
  title,
  isLoading,
  data,
  totalFuelConsumption,
  assetTypeKey,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const excessFuelColor = palette.primary;
  const normalFuelColor =
    CLIENT_THEME === 'yokohama' ? palette.grey : '#A776FF';
  const pieCursorColor =
    CLIENT_THEME === 'yokohama' ? palette.sectionBackground : '#1B2B4A';
  const { t } = useTranslation();
  const { unitSystem } = useCustomerInfo();
  const [activeIndex] = useState<number | null>(0);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);

  // Determine if the customer is "buz"
  const {
    customer: { code },
  } = useAppSelector((state) => state.authReducer);
  const { fleetDeviceDetails } = useAppSelector(
    (state) => state.fuelAnalyticsReducer
  );
  const isBuzzi = ['buz', 'myn'].includes(code);

  const eventToKeyMap: Record<string, string> = {
    Overspeeding: 'total_overspeed_fuel',
    Underspeeding: 'total_underspeed_fuel',
    'Incorrect Gear Speed Ratio': 'total_gear_fuel',
    'Short Idling': 'total_short_idle_fuel',
    'Long Idling': 'total_long_idle_fuel',
    'Queuing - Pre Loading': 'total_pre_loading_fuel',
    'Queuing - Pre Dumping': 'total_pre_dumping_fuel',
    'Road Conditions': 'total_road_condition_fuel',
    'Tire Underpressure': 'total_underpressure_fuel',
  };

  const eventToDataKeyMap: Record<string, string> = {
    Overspeeding: 'overspeed',
    Underspeeding: 'underspeed',
    'Incorrect Gear Speed Ratio': 'incorrect_gear',
    'Short Idling': 'short_idling',
    'Long Idling': 'long_idling',
    'Queuing - Pre Loading': 'pre_loading',
    'Queuing - Pre Dumping': 'pre_dumping',
    'Road Conditions': 'road_condition',
    'Tire Underpressure': 'tire_underpressure',
  };

  const handleEventClick = (eventName: string) => {
    if (eventToDataKeyMap[eventName]) {
      setSelectedEvent(eventName === selectedEvent ? null : eventName);
    }
  };

  const excessFuelUsageDiff = useMemo(() => {
    const keys = Object.values(eventToDataKeyMap);
    return keys.reduce(
      (sum, key) => sum + (Number(data[`${key}_diff`]) || 0),
      0
    );
  }, [data]);

  const excessFuelUsage = useMemo(() => {
    const keys = Object.values(eventToDataKeyMap);
    return keys.reduce((sum, key) => sum + (Number(data[key]) || 0), 0);
  }, [data]);

  const tableData = [
    {
      name: 'Excess Fuel Usage',
      displayName: getFuelUsageEventLabel(t, 'Excess Fuel Usage'),
      time: formatNumberToShortScale(truncateTo2dp(excessFuelUsage), 2),
      direction:
        excessFuelUsageDiff === 0
          ? 'none'
          : excessFuelUsageDiff > 0
          ? 'up'
          : 'down',
      color: excessFuelColor,
      fill: excessFuelColor,
      maxValue: excessFuelUsage,
    },
  ];

  const fuelUnit = useMemo(
    () => (unitSystem === 'metric' ? 'L' : 'Gal'),
    [unitSystem]
  );

  const dataList = useMemo(() => {
    const breakdown = {
      'Excess Fuel Usage': [
        {
          name: 'Overspeeding',
          displayName: getFuelUsageEventLabel(t, 'Overspeeding'),
          value: formatNumberToShortScale(
            Number(truncateTo2dp(data.overspeed)),
            2
          ),
          direction:
            data?.overspeed_diff === 0
              ? 'none'
              : data?.overspeed_diff > 0
              ? 'up'
              : 'down',
          color: excessFuelColor,
        },
        {
          name: 'Underspeeding',
          displayName: getFuelUsageEventLabel(t, 'Underspeeding'),
          value: formatNumberToShortScale(
            Number(truncateTo2dp(data.underspeed)),
            2
          ),
          direction:
            data?.underspeed_diff === 0
              ? 'none'
              : data?.underspeed_diff > 0
              ? 'up'
              : 'down',
          color: '#FF0068',
        },
        {
          name: 'Incorrect Gear Speed Ratio',
          displayName: getFuelUsageEventLabel(t, 'Incorrect Gear Speed Ratio'),
          value: formatNumberToShortScale(
            Number(truncateTo2dp(data.incorrect_gear)),
            2
          ),
          direction:
            data?.incorrect_gear_diff === 0
              ? 'none'
              : data?.incorrect_gear_diff > 0
              ? 'up'
              : 'down',
          color: '#A776FF',
        },
        {
          name: 'Short Idling',
          displayName: getFuelUsageEventLabel(t, 'Short Idling'),
          value: formatNumberToShortScale(truncateTo2dp(data.short_idling), 2),
          direction:
            data?.short_idling_diff === 0
              ? 'none'
              : data?.short_idling_diff > 0
              ? 'up'
              : 'down',
          color: '#FF9C2C',
        },
        {
          name: 'Long Idling',
          displayName: getFuelUsageEventLabel(t, 'Long Idling'),
          value: formatNumberToShortScale(truncateTo2dp(data.long_idling), 2),
          direction:
            data?.long_idling_diff === 0
              ? 'none'
              : data?.long_idling_diff > 0
              ? 'up'
              : 'down',
          color: '#FF0000',
        },
        ...(assetTypeKey !== 'loader'
          ? [
              {
                name: 'Queuing - Pre Dumping',
                displayName: getFuelUsageEventLabel(t, 'Queuing - Pre Dumping'),
                value: formatNumberToShortScale(
                  truncateTo2dp(data.pre_dumping),
                  2
                ),
                direction:
                  data?.pre_dumping_diff === 0
                    ? 'none'
                    : data?.pre_dumping_diff > 0
                    ? 'up'
                    : 'down',
                color: '#FFAAAA',
              },
              {
                name: 'Queuing - Pre Loading',
                displayName: getFuelUsageEventLabel(t, 'Queuing - Pre Loading'),
                value: formatNumberToShortScale(
                  truncateTo2dp(data.pre_loading),
                  2
                ),
                direction:
                  data?.pre_loading_diff === 0
                    ? 'none'
                    : data?.pre_loading_diff > 0
                    ? 'up'
                    : 'down',
                color: '#CBE4FF',
              },
            ]
          : []),
        ...(isBuzzi
          ? [
              {
                name: 'Road Conditions',
                displayName: getFuelUsageEventLabel(t, 'Road Conditions'),
                value: formatNumberToShortScale(
                  truncateTo2dp(data.road_condition),
                  2
                ),
                direction:
                  data?.road_condition_diff === 0
                    ? 'none'
                    : data?.road_condition_diff > 0
                    ? 'up'
                    : 'down',
                color: '#29BF59',
              },
            ]
          : []), // Only include Road Conditions if isBuzzi is true [Open Pit Customers]
        {
          name: 'Tire Underpressure',
          displayName: getFuelUsageEventLabel(t, 'Tire Underpressure'),
          value: formatNumberToShortScale(
            Number(truncateTo2dp(data.tire_underpressure)),
            2
          ),
          direction:
            data?.tire_underpressure_diff === 0
              ? 'none'
              : data?.tire_underpressure_diff > 0
              ? 'up'
              : 'down',
          color: '#00C6AE',
        },
      ],
    };
    breakdown['Excess Fuel Usage'] = breakdown['Excess Fuel Usage'].filter(
      (item) => {
        if (assetTypeKey === 'loader') {
          return (
            item.name !== 'Queuing - Pre Dumping' &&
            item.name !== 'Queuing - Pre Loading'
          );
        } // temporarily removed filter for loaders
        if (assetTypeKey === 'fleet' || assetTypeKey === 'hauler') return true;
        else {
          return (
            item.name !== 'Overspeeding' &&
            item.name !== 'Underspeeding' &&
            item.name !== 'Incorrect Gear Speed Ratio'
          );
        }
      }
    );
    return breakdown;
  }, [data, assetTypeKey, t]);

  const assetBreakdownData = useMemo(() => {
    const colors = [
      excessFuelColor,
      '#FF0068',
      '#A776FF',
      '#FF9C2C',
      '#FF0000',
      '#29BF59',
      '#FFAAAA',
      '#CBE4FF',
      '#FCD34D',
      '#4ADE80',
    ];

    const targetAssets = fleetDeviceDetails.filter((item: any) => {
      if (assetTypeKey === 'hauler') return item.asset_type === 'haul_truck';
      if (assetTypeKey === 'loader') return item.asset_type === 'loader';
      return true;
    });

    let rawBreakdown;
    if (selectedEvent && eventToKeyMap[selectedEvent]) {
      const key = eventToKeyMap[selectedEvent];
      rawBreakdown = targetAssets.map((item: any) => ({
        name: item.bumper_number || item.device,
        value: Number(truncateTo2dp(Number(item[key]) || 0)),
      }));
    } else {
      // Overall Excess Fuel Breakdown
      rawBreakdown = targetAssets.map((item: any) => {
        const totalValue = Object.values(eventToKeyMap).reduce(
          (sum, key) => sum + (Number(item[key]) || 0),
          0
        );
        return {
          name: item.bumper_number || item.device,
          value: Number(truncateTo2dp(totalValue)),
        };
      });
    }

    const filteredBreakdown = rawBreakdown
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);

    if (filteredBreakdown.length === 0) return [];

    const totalValue = filteredBreakdown.reduce((sum, d) => sum + d.value, 0);
    const threshold = totalValue * 0.02;

    const mainItems = filteredBreakdown.filter(
      (item) => item.value >= threshold
    );
    const othersItems = filteredBreakdown.filter(
      (item) => item.value < threshold
    );

    const finalBreakdown = [...mainItems];
    if (othersItems.length > 0) {
      finalBreakdown.push({
        name: t('fuel_usage_others'),
        value: othersItems.reduce((sum, d) => sum + d.value, 0),
      });
    }

    return finalBreakdown.map((item, index) => ({
      ...item,
      color: colors[index % colors.length],
    }));
  }, [selectedEvent, fleetDeviceDetails, assetTypeKey, excessFuelColor, t]);

  const displayedExcessValue = useMemo(() => {
    if (selectedEvent && eventToDataKeyMap[selectedEvent]) {
      return Number(data[eventToDataKeyMap[selectedEvent]]) || 0;
    }
    return excessFuelUsage;
  }, [selectedEvent, data, excessFuelUsage]);

  const pieData = [
    {
      name: getFuelUsageEventLabel(t, 'Excess Fuel Usage'),
      value: Number(truncateTo2dp(excessFuelUsage)),
      direction: Number(truncateTo2dp(excessFuelUsage)) === 0 ? 'none' : 'up',
      color: excessFuelColor,
    },
    {
      name: t('fuel_usage_normal_fuel_usage_label'),
      value: Number(truncateTo2dp(totalFuelConsumption - excessFuelUsage)),
      direction:
        Number(truncateTo2dp(totalFuelConsumption - excessFuelUsage)) === 0
          ? 'none'
          : 'up',
      color: normalFuelColor,
    },
  ];

  return (
    <Grid item lg={12} md={6}>
      <Box
        sx={{
          padding: 2,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.cardBorderColor}`,
          minHeight: '300px',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Box sx={{ flex: 1 }} />
        </Box>

        <Box>
          {isLoading ? (
            <MultiColorCircularLoader height="300px" />
          ) : (
            <Box sx={{ gap: 2, alignItems: 'flex-start' }}>
              {/* Chart Section */}
              <Box
                sx={{
                  width: '100%',
                  gap: 2,
                  display: 'grid',
                  gridTemplateColumns:
                    'minmax(200px, 1fr) minmax(280px, 1.4fr) minmax(200px, 1fr)',
                }}
              >
                {/* evenly divided columns with 10px gap */}
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mb: 1,
                      position: 'relative',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: '600',
                        textAlign: 'center',
                        color: enableDarkTheme ? 'white' : 'black',
                      }}
                    >
                      {t('normal_fuel_usage')}{' '}
                      {formatNumberToShortScale(
                        truncateTo2dp(totalFuelConsumption - excessFuelUsage),
                        2
                      )}{' '}
                      {fuelUnit}
                    </Typography>
                  </Box>
                  <FlexBox
                    sx={{
                      height: '210px',
                      width: '100%',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {data.engineHours === 0 ? (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          textAlign: 'center',
                          color: enableDarkTheme ? '#9CA3AF' : '#6B7280',
                        }}
                      >
                        {t('no_data_for_selected_range')}
                      </Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart
                          margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
                        >
                          <Pie
                            data={pieData}
                            dataKey="value"
                            cx="50%"
                            cy="50%"
                            activeIndex={
                              activeIndex !== null ? [activeIndex] : []
                            }
                            activeShape={renderActiveShape}
                            innerRadius={'40%'}
                            outerRadius="70%"
                            paddingAngle={5}
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Legend
                            layout="horizontal"
                            verticalAlign="bottom"
                            align="center"
                            iconSize={8}
                            wrapperStyle={{
                              fontSize: '10px',
                              paddingTop: '20px',
                              width: '100%',
                            }}
                          />
                          <Tooltip
                            cursor={{ fill: pieCursorColor }}
                            wrapperStyle={{
                              outline: 'none',
                              fontSize: '11px',
                            }}
                            contentStyle={{
                              backgroundColor: palette.boxBackground,
                              border: `1px solid ${palette.cardBorderColor}`,
                              color: palette.textPrimary,
                            }}
                            itemStyle={{ color: palette.textPrimary }}
                            labelStyle={{ color: palette.textPrimary }}
                            formatter={(value: any) => {
                              return `${truncateTo2dp(value)} ${
                                unitSystem === 'metric' ? 'L' : 'gal'
                              }`;
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </FlexBox>
                </Box>
                <Box>
                  <TableList
                    data={tableData}
                    subItems={dataList}
                    unit={
                      unitSystem === 'metric'
                        ? t('fuel_usage_unit_litres')
                        : t('fuel_usage_unit_gallons')
                    }
                    onItemClick={handleEventClick}
                    selectedItemName={selectedEvent}
                  />
                </Box>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mb: 1,
                      position: 'relative',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: '600',
                        textAlign: 'center',
                        color: enableDarkTheme ? 'white' : 'black',
                      }}
                    >
                      {selectedEvent
                        ? `${getFuelUsageEventLabel(t, selectedEvent)}: `
                        : `${getFuelUsageEventLabel(t, 'Excess Fuel Usage')}: `}
                      {formatNumberToShortScale(
                        truncateTo2dp(displayedExcessValue),
                        2
                      )}{' '}
                      {fuelUnit}
                    </Typography>
                    {selectedEvent && (
                      <Box
                        onClick={() => {
                          setSelectedEvent(null);
                        }}
                        sx={{
                          position: 'absolute',
                          right: 0,
                          fontSize: '10px',
                          color: '#EB5757',
                          cursor: 'pointer',
                          fontWeight: '600',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          border: '1px solid rgba(235, 87, 87, 0.5)',
                          backgroundColor: 'rgba(235, 87, 87, 0.1)',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: 'rgba(235, 87, 87, 0.2)',
                            borderColor: '#EB5757',
                          },
                        }}
                      >
                        {t('fuel_usage_reset')}
                      </Box>
                    )}
                  </Box>
                  <FlexBox
                    sx={{
                      height: '210px',
                      width: '100%',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {selectedEvent === 'Road Conditions' ? (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          textAlign: 'center',
                          color: enableDarkTheme ? '#9CA3AF' : '#6B7280',
                        }}
                      >
                        {t('excess_fuel_loss_due_to_road_conditions')} <br />{' '}
                        {t('is_currently_tracked_at_the_fleet_level')}
                      </Typography>
                    ) : assetBreakdownData.length === 0 ? (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          textAlign: 'center',
                          color: enableDarkTheme ? '#9CA3AF' : '#6B7280',
                        }}
                      >
                        {selectedEvent
                          ? t('fuel_usage_no_assets_for_event', {
                              event: getFuelUsageEventLabel(t, selectedEvent),
                            })
                          : t('fuel_usage_no_asset_data')}
                      </Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart
                          margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
                        >
                          <Pie
                            data={assetBreakdownData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            activeIndex={
                              activeIndex !== null ? [activeIndex] : []
                            }
                            activeShape={renderActiveShape}
                            innerRadius={'40%'}
                            outerRadius="70%"
                            paddingAngle={5}
                          >
                            {assetBreakdownData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: palette.boxBackground,
                              border: `1px solid ${palette.cardBorderColor}`,
                              color: palette.textPrimary,
                            }}
                            itemStyle={{ color: palette.textPrimary }}
                            labelStyle={{ color: palette.textPrimary }}
                            formatter={(value: any) =>
                              `${truncateTo2dp(value)} ${
                                unitSystem === 'metric' ? 'L' : 'gal'
                              }`
                            }
                          />
                          <Legend
                            layout="horizontal"
                            verticalAlign="bottom"
                            align="center"
                            iconSize={8}
                            wrapperStyle={{
                              fontSize: '10px',
                              paddingTop: '20px',
                              width: '100%',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </FlexBox>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default FuelUsageBreakdown;

const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props;

  return (
    <g>
      {/* Enlarged active segment */}
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={Number(outerRadius) + 20}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />

      {/* label */}
      {/* <text
        x={cx}
        y={cy}
        dy={8}
        fill={enableDarkTheme ? '#F3F4F6' : palette.cardBorderColor}
        fontSize={14}
        fontWeight={600}
      >
        {`${payload.name}: `}
        <tspan fontSize={12} fill={fill} dx={2}>
          {formatNumberToShortScale(payload.value)}{' '}
          {unitSystem === 'metric' ? 'L' : 'gal'}
        </tspan>
      </text> */}
    </g>
  );
};
